import {
  ConfigurationResponseType,
  ConfigurationType,
} from 'V2/Types/Configuration';

import { formatOptions } from '../formatters';

function isConfigurationResponseTransformed(
  value: ConfigurationResponseType | ConfigurationType
): value is ConfigurationType {
  return (
    value.hasOwnProperty('drivers') &&
    value.hasOwnProperty('vehicles') &&
    value.hasOwnProperty('coverages')
  );
}

export const transformConfigResponse = (
  response: ConfigurationResponseType
): ConfigurationType => {
  if (isConfigurationResponseTransformed(response)) {
    return response;
  }

  return {
    drivers: {
      allowExcludedDrivers:
        response.insurableParty.allowExcludedDrivers ?? true,
      accidents: {
        challengeReasonOptions: formatOptions(
          response.insurableParty.accidents.challengeReasonOptions
        ),
        collectLossInformation:
          response.insurableParty.accidents.collectLossInformation,
        collectAccidentType:
          response.insurableParty.accidents.collectAccidentType,
        lookbackYears: response.insurableParty.accidents.lookbackYears ?? 'few',
        lossOptions: formatOptions(
          response.insurableParty.accidents.lossOptions
        ),
        accidentTypeOptions: formatOptions(
          response.insurableParty.accidents.accidentTypeOptions
        ),
        month: formatOptions(response.insurableParty.accidents.month),
        year: formatOptions(response.insurableParty.accidents.year),
        typeOfLoss: {
          required:
            response.insurableParty.accidents.typeOfLoss?.required ?? false,
          options: formatOptions(
            response.insurableParty.accidents.typeOfLoss?.options
          ),
        },
        showLendingLosses: response.insurableParty.accidents.showLendingLosses,
        assumeAtFault: response.insurableParty.accidents.assumeAtFault,
        collectDayOfAccident:
          response.insurableParty.accidents.collectDayOfAccident,
        driverClaimTypes: formatOptions(
          response.insurableParty.accidents.driverClaimTypes
        ),
        nonListedDriverClaimTypes: formatOptions(
          response.insurableParty.accidents.nonListedDriverClaimTypes
        ),
        collectTypeOfLoss: response.insurableParty.accidents.collectTypeOfLoss,
      },
      currentInsurance: {
        biLevel: formatOptions(
          response.insurableParty.currentInsurance.biLevel
        ),
        carrierName: formatOptions(
          response.insurableParty.currentInsurance.carrierName
        ),
        lapseDuration: formatOptions(
          response.insurableParty.currentInsurance.lapseDuration
        ),
        lapseReason: formatOptions(
          response.insurableParty.currentInsurance.lapseReason
        ),
        insuredDuration: formatOptions(
          response.insurableParty.currentInsurance.insuredDuration
        ),
      },
      driverLicense: {
        state: formatOptions(response.insurableParty.driverLicense.state),
        status: formatOptions(response.insurableParty.driverLicense.status),
        verified: {
          required:
            response.insurableParty.driverLicense.verified?.required ?? false,
        },
        hasForeignLicense: {
          required:
            response.insurableParty.driverLicense.hasForeignLicense.required ??
            false,
        },
        hasLearnersPermit: {
          required:
            response.insurableParty.driverLicense.hasLearnersPermit.required ??
            false,
        },
      },
      stateResidency: {
        required: response.insurableParty.stateResidency.required ?? false,
      },
      drivingHistory: {
        previousSuspensionCount: formatOptions(
          response.insurableParty.drivingHistory.previousSuspensionCount
        ),
        previousSuspensionDate: formatOptions(
          response.insurableParty.drivingHistory.previousSuspensionDate
        ),
      },
      education: {
        currentLevel: formatOptions(
          response.insurableParty.education.currentLevel
        ),
      },
      exclusionReason: formatOptions(response.insurableParty.exclusionReason),
      maxDrivers: response.insurableParty.maxDrivers ?? '',
      militaryService: {
        militaryRank: formatOptions(
          response.insurableParty.militaryService.militaryRank
        ),
        serviceType: formatOptions(
          response.insurableParty.militaryService.serviceType
        ),
      },
      personalInfo: {
        gender: formatOptions(response.insurableParty.personalInfo.gender),
        maritalStatus: formatOptions(
          response.insurableParty.personalInfo.maritalStatus
        ),
        relationshipToApplicant: formatOptions(
          response.insurableParty.personalInfo.relationshipToApplicant
        ),
        suffix: formatOptions(response.insurableParty.personalInfo.suffix),
      },
      profession: formatOptions(response.insurableParty.profession),
      violations: {
        lookbackYears:
          response.insurableParty.violations.lookbackYears ?? 'few',
        month: formatOptions(response.insurableParty.violations.month),
        day: formatOptions(response.insurableParty.violations.day),
        violationType: formatOptions(
          response.insurableParty.violations.violationType
        ),
        year: formatOptions(response.insurableParty.violations.year),
        challengeReasonOptions: formatOptions(
          response.insurableParty.violations.challengeReasonOptions
        ),
      },
      removalReason: formatOptions(response.insurableParty.removalReason),
      goodStudentDiscount: response.insurableParty.goodStudentDiscount,
      homeownerVerification: response.insurableParty.homeownerVerification,
      statusDetails: response.insurableParty.statusDetails,
    },
    vehicles: {
      lienHolderAddresses: response.insurableItem.lienholders.reduce(
        (acc, bank) => {
          return {
            ...acc,
            [bank.name.value]: { ...bank.address },
          };
        },
        {}
      ),
      lienHolders: response.insurableItem.lienholders.reduce((acc, bank) => {
        return {
          ...acc,
          [bank.name.value]: bank.name.label,
        };
      }, {}),
      usage: formatOptions(response.insurableItem.usage),
      ownership: {
        ownershipType: formatOptions(
          response.insurableItem.ownership.ownershipType
        ),
      },
      parkingDetails: {
        inStateParkingDuration: {
          required:
            response.insurableItem.parkingDetails?.inStateParkingDuration
              ?.required ?? false,
          options: formatOptions(
            response.insurableItem.parkingDetails?.inStateParkingDuration
              ?.options ?? []
          ),
        },
        parkingLocationReason: {
          required:
            response.insurableItem.parkingDetails?.parkingLocationReason
              ?.required ?? false,
          options: formatOptions(
            response.insurableItem.parkingDetails?.parkingLocationReason
              ?.options ?? []
          ),
        },
      },
      parkingType: {
        required: response.insurableItem.parkingType?.required ?? false,
        options: formatOptions(
          response.insurableItem.parkingType?.options ?? []
        ),
      },
      rideshareUse: {
        required: response.insurableItem.rideshareUse?.required ?? false,
      },
      usageDetails: {
        required: response.insurableItem.usageDetails?.required ?? false,
        usedInMexico: {
          required:
            response.insurableItem.usageDetails?.usedInMexico?.required ??
            false,
        },
      },
      insurableItemCoverages: response.coverages.insurableItem.map((c) => ({
        ...c,
        coverageLevels: formatOptions(c.coverageLevels),
      })),
    },
    coverages: {
      insurableItem: response.coverages.insurableItem.map((c) => ({
        ...c,
        coverageLevels: formatOptions(c.coverageLevels),
      })),
      insuranceProduct: response.coverages.insuranceProduct.map((c) => ({
        ...c,
        coverageLevels: formatOptions(c.coverageLevels),
      })),
    },
    agencyInformation: {
      agencyBundle: {
        carrier: formatOptions(response.agencyInformation.agencyBundle.carrier),
        policyType: formatOptions(
          response.agencyInformation.agencyBundle.policyType
        ),
      },
    },
    effectiveDates: {
      startDate: response.effectiveDates?.startDate ?? undefined,
      endDate: response.effectiveDates?.endDate ?? undefined,
    },
    telematics: {
      enabled: response.telematics.enabled,
      required: response.telematics.required,
      enrollmentWindow: response.telematics.enrollmentWindow,
    },
    flexiblePayments: {
      enabled: response.flexiblePayments.enabled,
      paymentFrequencies: response.flexiblePayments.paymentFrequencies,
    },
    accessEnabled: response.accessEnabled ?? false,
    predictableProtectionFeeApplicable:
      response.predictableProtectionFeeApplicable ?? false,
  };
};
